/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

// Dialog di conferma a tema (sostituisce window.confirm e il suo "app.tascommand
// dice" del browser). API imperativa promise-based:
//
//   const ok = await confirmDialog({ title, message, confirmLabel, danger, transition });
//
// Monta da sé un root React su document.body alla prima chiamata, così funziona
// in qualsiasi albero (MainApp e i pannelli admin/ROP/DirReg) senza provider.

import React, { useEffect, useReducer } from "react";
import { createRoot } from "react-dom/client";

const STYLE_ID = "tc-confirm-style";
const STYLE = `
.tc-overlay{position:fixed;inset:0;background:rgba(3,6,12,.62);backdrop-filter:blur(3px);
  display:flex;align-items:center;justify-content:center;padding:24px;opacity:0;pointer-events:none;
  transition:opacity .18s ease;z-index:4000;
  font-family:'Calibri',system-ui,-apple-system,'Segoe UI',sans-serif}
.tc-overlay.open{opacity:1;pointer-events:auto}
.tc-dialog{width:100%;max-width:420px;background:#0e1524;border:1px solid rgba(255,255,255,.08);
  border-radius:16px;overflow:hidden;
  box-shadow:0 24px 60px -12px rgba(0,0,0,.7),0 0 0 1px rgba(255,255,255,.02);
  transform:translateY(8px) scale(.98);transition:transform .18s ease}
.tc-overlay.open .tc-dialog{transform:translateY(0) scale(1)}
.tc-head{display:flex;align-items:center;gap:11px;padding:18px 20px 14px}
.tc-icon{width:34px;height:34px;border-radius:9px;flex:0 0 auto;display:flex;align-items:center;justify-content:center;
  background:rgba(244,194,71,.12);border:1px solid rgba(244,194,71,.3)}
.tc-icon svg{width:18px;height:18px;stroke:#f4c247}
.tc-icon.danger{background:rgba(201,27,27,.14);border-color:rgba(201,27,27,.4)}
.tc-icon.danger svg{stroke:#ef6b6b}
.tc-eyebrow{font-family:'Consolas',ui-monospace,Menlo,monospace;font-size:10.5px;letter-spacing:.16em;
  text-transform:uppercase;color:#C91B1B;font-weight:700;margin:0 0 2px}
.tc-title{font-family:'Consolas',ui-monospace,Menlo,monospace;font-size:15px;color:#e7edf6;font-weight:600;margin:0;letter-spacing:.01em}
.tc-body{padding:4px 20px 20px}
.tc-question{font-size:14.5px;color:#8794a8;line-height:1.45;margin:0;white-space:pre-line}
.tc-question .tc-zone{color:#e7edf6;font-family:'Consolas',ui-monospace,Menlo,monospace;font-weight:600}
.tc-trans{display:flex;align-items:center;gap:12px;background:#0b1120;border:1px solid rgba(255,255,255,.08);
  border-radius:11px;padding:13px 14px;margin-top:16px}
.tc-badge{font-family:'Consolas',ui-monospace,Menlo,monospace;font-size:11px;font-weight:700;letter-spacing:.09em;
  text-transform:uppercase;padding:6px 11px;border-radius:7px;border:1px solid;white-space:nowrap}
.tc-badge.amber{color:#f4c247;background:rgba(244,194,71,.12);border-color:rgba(244,194,71,.35)}
.tc-badge.green{color:#37d399;background:rgba(55,211,153,.12);border-color:rgba(55,211,153,.35)}
.tc-badge.gray{color:#9aa6ba;background:rgba(154,166,186,.12);border-color:rgba(154,166,186,.35)}
.tc-badge.red{color:#ef6b6b;background:rgba(201,27,27,.14);border-color:rgba(201,27,27,.4)}
.tc-arrow{color:#8794a8;flex:0 0 auto;display:flex}
.tc-arrow svg{width:18px;height:18px;stroke:currentColor}
.tc-foot{display:flex;gap:10px;justify-content:flex-end;padding:14px 20px 18px;border-top:1px solid rgba(255,255,255,.08)}
.tc-btn{font-family:'Consolas',ui-monospace,Menlo,monospace;font-size:12.5px;letter-spacing:.05em;font-weight:600;
  text-transform:uppercase;padding:10px 18px;border-radius:9px;cursor:pointer;border:1px solid transparent;transition:all .14s ease}
.tc-btn-ghost{background:transparent;border-color:rgba(255,255,255,.14);color:#8794a8}
.tc-btn-ghost:hover{color:#e7edf6;border-color:#e7edf6}
.tc-btn-confirm{background:#37d399;border-color:#37d399;color:#04150e}
.tc-btn-confirm:hover{filter:brightness(1.08)}
.tc-btn-confirm.danger{background:#C91B1B;border-color:#C91B1B;color:#fff}
`;

// ── Store esterno minimale ──────────────────────────────────────────────────
let current = null;          // { opts, resolve }
const subs = new Set();
const emit = () => subs.forEach((fn) => fn());

function resolveWith(val) {
  if (!current) return;
  const { resolve } = current;
  current = null;
  emit();
  resolve(val);
}

const WarnIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);

function ConfirmHost() {
  const [, force] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const fn = () => force();
    subs.add(fn);
    return () => subs.delete(fn);
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (!current) return;
      if (e.key === "Escape") resolveWith(false);
      else if (e.key === "Enter") resolveWith(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const o = current?.opts || {};
  const open = !!current;
  const danger = !!o.danger;
  const tr = o.transition;

  return (
    <div className={`tc-overlay${open ? " open" : ""}`} onMouseDown={(e) => { if (e.target === e.currentTarget) resolveWith(false); }}>
      {open && (
        <div className="tc-dialog" role="alertdialog" aria-modal="true">
          <div className="tc-head">
            <div className={`tc-icon${danger ? " danger" : ""}`}><WarnIcon /></div>
            <div>
              <p className="tc-eyebrow">{o.eyebrow || "TAS Command"}</p>
              <h2 className="tc-title">{o.title || "Conferma"}</h2>
            </div>
          </div>
          <div className="tc-body">
            <p className="tc-question">{o.message}</p>
            {tr && (
              <div className="tc-trans">
                <span className={`tc-badge ${tr.from?.tone || "amber"}`}>{tr.from?.label}</span>
                <span className="tc-arrow"><ArrowIcon /></span>
                <span className={`tc-badge ${tr.to?.tone || "green"}`}>{tr.to?.label}</span>
              </div>
            )}
          </div>
          <div className="tc-foot">
            <button className="tc-btn tc-btn-ghost" onClick={() => resolveWith(false)}>{o.cancelLabel || "Annulla"}</button>
            <button className={`tc-btn tc-btn-confirm${danger ? " danger" : ""}`} autoFocus onClick={() => resolveWith(true)}>{o.confirmLabel || "Conferma"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

let hostMounted = false;
function ensureHost() {
  if (hostMounted || typeof document === "undefined") return;
  hostMounted = true;
  if (!document.getElementById(STYLE_ID)) {
    const st = document.createElement("style");
    st.id = STYLE_ID;
    st.textContent = STYLE;
    document.head.appendChild(st);
  }
  const el = document.createElement("div");
  document.body.appendChild(el);
  createRoot(el).render(<ConfirmHost />);
}

/**
 * Mostra il dialog di conferma a tema. Restituisce Promise<boolean>.
 * @param {object} opts
 * @param {string} [opts.title]        titolo (default "Conferma")
 * @param {React.ReactNode} opts.message  testo della domanda (supporta \n)
 * @param {string} [opts.confirmLabel] etichetta pulsante conferma
 * @param {string} [opts.cancelLabel]  etichetta pulsante annulla
 * @param {boolean} [opts.danger]      stile distruttivo (rosso) per le eliminazioni
 * @param {object} [opts.transition]   { from:{label,tone}, to:{label,tone} } per i cambi stato
 */
export function confirmDialog(opts = {}) {
  ensureHost();
  // Se un dialog è già aperto, lo annulla prima di aprire il nuovo.
  if (current) resolveWith(false);
  return new Promise((resolve) => {
    current = { opts, resolve };
    emit();
  });
}

export default confirmDialog;
